// src/pages/tasks/task-progress-page/TaskProgressPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
  Upload,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import { ApiRequest } from "../../../services/api/api";
import { getUserId } from "../../../services/lib/utils/auth.utils";
import { taskCommentTypeForApi } from "../task-comment-utils";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

type CommentType = "Routine" | "Apply Reschedule" | "Apply Completion";

interface TaskProgressForm {
  type: CommentType;
  content: string;
}

interface TaskProgressPageProps {
  taskId?: string;
}

const commentTypeOptions: CommentType[] = ["Routine", "Apply Reschedule", "Apply Completion"];

const TaskProgressPage: React.FC<TaskProgressPageProps> = (props) => {
  const params = useParams<{ taskId?: string }>();
  const history = useHistory();
  const [form] = Form.useForm<TaskProgressForm>();
  const taskId = params.taskId || props.taskId;

  const [taskTitle, setTaskTitle] = useState<string>("Task Title");
  const [participants, setParticipants] = useState<any[]>([]);
  const [recipientsLabel, setRecipientsLabel] = useState<string>("All Task Members");
  const [noticeContent, setNoticeContent] = useState<string>("Task Title Progress Update: Routine comment is added.");
  const [fileList, setFileList] = useState<any[]>([]);

  const loadTaskInfo = async () => {
    if (!taskId) {
      return;
    }

    try {
      const response = await ApiRequest({
        otherUrl: "TaskManagement/GetTaskById",
        type: "get",
        params: { taskId: parseInt(taskId, 10) },
      });
      const data = response?.data?.data ?? response?.data;
      if (!data) throw new Error("Task not found");
      const title = data.title ?? data.Title ?? `Task #${taskId}`;
      setTaskTitle(title);
      const taskParticipants = data.participants ?? data.Participants ?? [];
      setParticipants(taskParticipants);
      setRecipientsLabel("All Task Members");
      setNoticeContent(`${title} Progress Update: Routine comment is added.`);
      form.setFieldsValue({
        type: "Routine",
        content: "",
      });
    } catch (error: any) {
      message.error(error?.message || "Failed to load task");
    }
  };

  useEffect(() => {
    loadTaskInfo();
  }, [taskId]);

  useEffect(() => {
    document.title = "Task Progress";
  }, []);

  const handleTypeChange = (value: CommentType) => {
    const template = `${taskTitle} Progress Update: ${value} comment is added.`;
    setNoticeContent(template);
  };

  const uploadProps = useMemo(
    () => ({
      beforeUpload: () => false,
      fileList,
      onChange: ({ fileList: newList }: any) => setFileList(newList),
    }),
    [fileList]
  );

  const handleSubmit = async (values: TaskProgressForm) => {
    try {
      if (!taskId) {
        throw new Error("Task id is missing.");
      }
      const creatorRaw = getUserId();
      const createdBy = creatorRaw ? parseInt(String(creatorRaw), 10) : 0;
      if (!createdBy) {
        throw new Error("Current user is not identified.");
      }

      const notifyIds = participants
        .map((p: any) => p.userId ?? p.UserId)
        .filter((id: any) => !!id)
        .map((id: any) => parseInt(String(id), 10));

      await ApiRequest({
        otherUrl: "TaskComment/AddComment",
        method: "post",
        data: {
          taskId: parseInt(taskId, 10),
          createdBy,
          commentType: taskCommentTypeForApi(values.type),
          content: values.content,
          notifyUserIds: notifyIds,
        },
        isShowSpinner: true,
      });

      message.success("Comment added successfully");
      history.goBack();
    } catch (error: any) {
      console.error("Failed to add progress comment", error);
      const errMsg = error?.message;
      message.error(errMsg && errMsg !== "[object Object]" ? errMsg : "Failed to add comment");
    }
  };

  const handleNoticeEdit = () => {
    if (!taskId) return;
    history.push(`/tasks/${taskId}/notification/new`);
  };

  return (
    <div style={{ padding: "1.5rem", maxWidth: "1400px", margin: "0 auto" }}>
      <Row gutter={24} align="top">
        <Col xs={24} lg={24}>
          <Card>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Title level={3} style={{ marginBottom: 0 }}>
                Progress Update
              </Title>
              {taskId && (
                <Text type="secondary">Task ID: {taskId}</Text>
              )}
            </div>
            <Divider />

            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              <Form.Item
                label="Type"
                name="type"
                rules={[{ required: true, message: "Please select type" }]}
              >
                <Select
                  placeholder="Select comment type"
                  onChange={handleTypeChange}
                >
                  {commentTypeOptions.map((opt) => (
                    <Option key={opt} value={opt}>
                      {opt}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Content"
                name="content"
                rules={[{ required: true, message: "Please enter content" }]}
              >
                <TextArea rows={6} placeholder="Enter progress update..." />
              </Form.Item>

              <Form.Item label="Attachment">
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>Select File</Button>
                </Upload>
              </Form.Item>

              <Card
                style={{
                  background: "#fafafa",
                  borderColor: "#d9d9d9",
                  marginBottom: "1rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <Text strong>Notification</Text>
                    <div style={{ marginTop: "0.5rem" }}>
                      <Text type="secondary">{recipientsLabel}</Text>
                    </div>
                    <div>
                      <Text>{noticeContent}</Text>
                    </div>
                  </div>
                  <Button type="primary" onClick={handleNoticeEdit}>
                    Edit
                  </Button>
                </div>
              </Card>

              <Divider />
              <Button type="primary" htmlType="submit" size="large">
                Add Comment
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TaskProgressPage;


